import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import BackgroundVideo from "@/components/BackgroundVideo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck } from "lucide-react";

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser, fetchTeam } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.email) setEmail(r.data.email);
        // Inject team brand on this page
        const root = document.documentElement;
        if (r.data.team_primary) root.style.setProperty("--brand-primary", r.data.team_primary);
        if (r.data.team_secondary) root.style.setProperty("--brand-secondary", r.data.team_secondary);
      })
      .catch((e) => setErr(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, { email: email || undefined, password });
      setUser(data);
      if (data?.team_id) await fetchTeam();
      nav("/portal", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!info && !err) return <div className="min-h-screen flex items-center justify-center text-sm text-white/60" style={{ backgroundColor: "var(--ink-1)" }}>Loading invite…</div>;

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ backgroundColor: "var(--ink-1)" }} data-testid="invite-accept-page">
      <div className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden">
        <BackgroundVideo />
        <Logo size="md" variant="lockup" linkTo="/" className="relative z-10" dark />
        <div className="relative z-10 max-w-md">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>You're invited</div>
          <h2 className="font-display font-black text-5xl tracking-tighter mt-3 leading-[0.95]">
            Join your team's<br /> readiness console.
          </h2>
          {info && <p className="text-white/70 mt-6 leading-relaxed">
            <span className="text-white font-semibold">{info.team_name}</span> is inviting <span className="text-white font-semibold">{info.name}</span> to track daily readiness, view next-day hybrid tracks, and stay healthy through the season.
          </p>}
        </div>
        <div className="relative z-10 text-[11px] text-white/40 tracking-[0.25em] uppercase font-semibold">
          Healthy Arms · Healthy Bodies · Elite Performance
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="invite-accept-form">
          <div className="md:hidden mb-10"><Logo size="sm" variant="lockup" linkTo="/" dark /></div>
          {err && !info ? (
            <div className="rounded-md border border-red-900/50 bg-red-950/40 p-6 text-red-300" data-testid="invite-error">
              <div className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Invalid invite</div>
              <div className="mt-2 text-sm">{err}</div>
            </div>
          ) : info && (
            <>
              <div className="text-[10px] tracking-[0.3em] font-bold uppercase mb-3" style={{ color: "var(--brand-secondary)" }}>
                {info.team_name}
              </div>
              <h1 className="font-display font-black text-4xl tracking-tighter text-white">Welcome, {info.name.split(" ")[0]}.</h1>
              <p className="text-white/50 mt-2 text-sm">
                Set your account password to join <span className="text-white font-semibold">{info.team_name}</span>.
              </p>

              <div className="space-y-4 mt-8">
                <div>
                  <Label className="text-white/70">Email</Label>
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@team.com"
                    className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" data-testid="invite-email-input" />
                </div>
                <div>
                  <Label className="text-white/70">Choose a password</Label>
                  <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters"
                    className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" data-testid="invite-password-input" />
                </div>
                <div>
                  <Label className="text-white/70">Confirm password</Label>
                  <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" data-testid="invite-confirm-input" />
                </div>
                {err && <div className="text-sm text-red-400" data-testid="invite-error">{err}</div>}
                <Button type="submit" disabled={busy} className="w-full h-12 rounded-md text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="invite-submit-button">
                  {busy ? "Setting up…" : <>Activate account <ArrowRight className="ml-1.5 h-4 w-4" /></>}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
